import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAiDictationStore from '../stores/aiDictationStore';
import StatusBar from '../components/StatusBar';
import SelectionModal from '../components/SelectionModal';

const AiDictationTextbook = () => {
  const navigate = useNavigate();
  const { setWords, setSourceType } = useAiDictationStore();
  
  // 设置来源类型
  React.useEffect(() => {
    setSourceType('textbook');
  }, [setSourceType]);

  // 模态框状态
  const [modal, setModal] = useState({
    isOpen: false,
    type: null, // 'grade', 'term'
  });
  
  // 选择状态
  const [grade, setGrade] = useState('三年级');
  const [term, setTerm] = useState('上册');
  const [selectedUnits, setSelectedUnits] = useState([]);
  
  // 模拟课本单元数据
  const units = [
    { id: 1, name: 'Unit 1', words: ['apple', 'banana', 'orange'] },
    { id: 2, name: 'Unit 2', words: ['dog', 'cat', 'bird'] },
    { id: 3, name: 'Unit 3', words: ['red', 'blue', 'green'] },
    { id: 4, name: 'Unit 4', words: ['one', 'two', 'three'] },
    { id: 5, name: 'Unit 5', words: ['book', 'pen', 'pencil'] },
  ];
  
  // 打开模态框
  const openModal = (type) => {
    setModal({
      isOpen: true,
      type
    });
  };
  
  // 关闭模态框
  const closeModal = () => {
    setModal({
      isOpen: false,
      type: null
    });
  };
  
  // 切换单元选择
  const toggleUnit = (unitId) => {
    if (selectedUnits.includes(unitId)) {
      setSelectedUnits(selectedUnits.filter(id => id !== unitId));
    } else {
      setSelectedUnits([...selectedUnits, unitId]);
    }
  };
  
  // 确认选择
  const handleConfirm = () => {
    // 从选中的单元中提取单词
    const words = [];
    selectedUnits.forEach(unitId => {
      const unit = units.find(u => u.id === unitId);
      if (unit) {
        unit.words.forEach(word => {
          words.push({ id: Date.now() + Math.random(), word });
        });
      }
    });
    
    // 更新全局状态
    setWords(words);
    
    // 返回主页面
    navigate('/ai-dictation');
  };
  
  // 获取当前模态框的选项和选中值
  const getModalProps = () => {
    const modalOptions = {
      grade: ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
      term: ['上册', '下册'],
    };
    
    switch (modal.type) {
      case 'grade':
        return {
          title: '选择年级',
          options: modalOptions.grade,
          selectedOption: grade,
          onSelect: (value) => {
            setGrade(value);
            closeModal();
          }
        };
      case 'term':
        return {
          title: '选择学期',
          options: modalOptions.term,
          selectedOption: term,
          onSelect: (value) => {
            setTerm(value);
            closeModal();
          }
        };
      default:
        return {
          title: '',
          options: [],
          selectedOption: '',
          onSelect: () => {}
        };
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">
      {/* iOS Status Bar */}
      <StatusBar />

      {/* Main Content */}
      <div className="p-5 pb-24">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <Link to="/ai-dictation" className="text-blue-500">
            <i className="fa-solid fa-chevron-left mr-1"></i>
            返回
          </Link>
          <h2 className="text-xl font-bold">课本单元选择</h2>
          <div className="w-16"></div> {/* Spacer for alignment */}
        </div>

        {/* Textbook Selection */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">选择课本</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div 
              className="p-3 border-b border-gray-100 flex justify-between items-center"
              onClick={() => openModal('grade')}
            >
              <div className="font-medium">年级</div>
              <div className="flex items-center text-gray-600">
                <span>{grade}</span>
                <i className="fa-solid fa-chevron-right ml-2 text-xs text-gray-400"></i>
              </div>
            </div>
            <div 
              className="p-3 flex justify-between items-center"
              onClick={() => openModal('term')}
            >
              <div className="font-medium">学期</div>
              <div className="flex items-center text-gray-600">
                <span>{term}</span>
                <i className="fa-solid fa-chevron-right ml-2 text-xs text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Unit Selection */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">选择单元</h3>
            <div className="text-green-600 text-sm font-medium">
              已选 {selectedUnits.length} 个单元
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="space-y-2">
              {units.map((unit) => (
                <div 
                  key={unit.id}
                  className={`p-3 rounded-lg border ${
                    selectedUnits.includes(unit.id) 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200'
                  } flex justify-between items-center cursor-pointer`}
                  onClick={() => toggleUnit(unit.id)}
                >
                  <div>
                    <div className="font-medium">{unit.name}</div>
                    <div className="text-xs text-gray-500">{unit.words.length} 个单词</div>
                  </div>
                  {selectedUnits.includes(unit.id) && (
                    <i className="fa-solid fa-check text-green-500"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <button 
          className="w-full bg-green-600 text-white py-4 rounded-xl font-bold text-lg shadow-md flex items-center justify-center"
          onClick={handleConfirm}
          disabled={selectedUnits.length === 0}
        >
          <i className="fa-solid fa-check mr-2"></i>
          确认选择
        </button>
      </div>

      {/* 选择模态框 */}
      <SelectionModal 
        isOpen={modal.isOpen}
        onClose={closeModal}
        {...getModalProps()}
      />
    </div>
  );
};

export default AiDictationTextbook;